// src/pages/ChatPage.js
import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Send, ShieldAlert, Image as ImageIcon } from "lucide-react";
import socket from "../socket";
import WorkerSidebar from "../components/WorkerSidebar";

const normalizeId = (id) =>
  typeof id === "string" ? id : id?._id || id?.id || (id ? String(id) : "");

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:5000";
const API_CHAT = `${API_BASE}/api/chat`;
const API_CUST = `${API_BASE}/api/customers`;

const ChatPage = () => {
  const { sender: workerId, receiver: customerId, jobId } = useParams();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [jobDetails, setJobDetails] = useState(null);
  const [bidDetails, setBidDetails] = useState(null);
  const chatBoxRef = useRef(null);

  // 🔥 NEW — fullscreen image viewer (for chat images & profile picture)
  const [fullscreenImage, setFullscreenImage] = useState(null);

  const roomId = [customerId, workerId].sort().join("-") + `-${jobId}`;

  useEffect(() => {
    if (!customerId || !workerId || !jobId) return;

    socket.emit("joinRoom", roomId);
    fetchChatHistory();
    fetchCustomerInfo();
    fetchJobDetails();
    fetchBidDetails();

    const handleReceive = (msg) => {
      if (String(msg.jobId) === String(jobId)) {
        if (msg.senderId === workerId) return;
        setMessages((prev) => [...prev, msg]);
        scrollToBottom();
      }
    };

    const handleTyping = (data) => {
      if (data.roomId === roomId && data.senderId !== workerId) {
        setIsTyping(true);
        setTimeout(() => setIsTyping(false), 1500);
      }
    };

    socket.on("receiveMessage", handleReceive);
    socket.on("typing", handleTyping);

    return () => {
      socket.off("receiveMessage", handleReceive);
      socket.off("typing", handleTyping);
    };
  }, [customerId, workerId, jobId]);

  const fetchChatHistory = async () => {
    try {
      const res = await axios.get(`${API_CHAT}/${customerId}/${workerId}/${jobId}`);
      setMessages(res.data || []);
      scrollToBottom();
    } catch (err) {
      console.error("❌ Chat history fetch failed", err);
    }
  };

  const fetchCustomerInfo = async () => {
    try {
      const res = await axios.get(`${API_CUST}/profile/${customerId}`);
      setCustomerInfo(res.data);
    } catch (err) {
      console.error("❌ Customer info fetch failed", err);
    }
  };

  const fetchJobDetails = async () => {
    try {
      const res = await axios.get(`${API_CUST}/jobs/job/${jobId}`);
      setJobDetails(res.data);
    } catch (err) {
      console.error("❌ Job details fetch failed", err);
    }
  };

  const fetchBidDetails = async () => {
    try {
      const res = await axios.get(`${API_CUST}/jobs/bid/${jobId}/${workerId}`);
      setBidDetails(res.data);
    } catch {
      console.warn("⚠️ No bid found");
    }
  };

  const handleTyping = (e) => {
    setInput(e.target.value);
    socket.emit("typing", { roomId, senderId: workerId });
  };

  const sendMessage = async () => {
    if (!input.trim()) return;
    const payload = {
      senderId: workerId,
      receiverId: customerId,
      message: input,
      jobId,
      roomId,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, payload]);
    setInput("");
    scrollToBottom();

    try {
      socket.emit("sendMessage", payload);
      await axios.post(`${API_CHAT}/send`, payload);
    } catch (err) {
      console.error("❌ Error sending message", err);
    }
  };

  // 🔥 IMAGE UPLOAD
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("image", file);
    formData.append("senderId", workerId);
    formData.append("receiverId", customerId);
    formData.append("jobId", jobId);
    formData.append("roomId", roomId);

    try {
      const res = await axios.post(`${API_CHAT}/upload-image`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const msg = res.data.message;
      setMessages((prev) => [...prev, msg]);
      scrollToBottom();
    } catch (err) {
      console.error("❌ Image upload failed", err);
    }
  };

  const scrollToBottom = () =>
    setTimeout(() => {
      if (chatBoxRef.current)
        chatBoxRef.current.scrollTo({
          top: chatBoxRef.current.scrollHeight,
          behavior: "smooth",
        });
    }, 100);

  const formatTime = (time) => {
    if (!time) return "";
    const parsed = new Date(time);
    if (isNaN(parsed.getTime())) return "";
    return parsed.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-blue-50 to-gray-100">
      <WorkerSidebar />

      <div className="flex-1 ml-0 md:ml-60 p-6 flex flex-col max-w-3xl mx-auto">
        {customerInfo && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="text-center mb-6 bg-white/70 backdrop-blur-md border border-blue-100 rounded-2xl p-6 shadow-lg"
          >
            <img
              src={
                customerInfo.profilePicture
                  ? `${API_BASE}${customerInfo.profilePicture}`
                  : "/default-avatar.png"
              }
              alt="Customer"
              className="w-20 h-20 mx-auto rounded-full border-2 border-blue-600 shadow hover:scale-105 transition-transform cursor-pointer"
              onClick={() =>
                setFullscreenImage(
                  customerInfo.profilePicture
                    ? `${API_BASE}${customerInfo.profilePicture}`
                    : "/default-avatar.png"
                )
              }
            />
            <h2 className="mt-3 text-xl font-semibold text-gray-800">
              {customerInfo.name}
            </h2>
            <p className="text-gray-500 text-sm">
              {jobDetails?.jobTitle || "Service"} •{" "}
              {bidDetails?.price ? `$${bidDetails.price}` : "Pending"}
            </p>

            <div className="mt-4 bg-yellow-100 text-yellow-800 px-4 py-2 rounded-lg text-sm flex items-center justify-center gap-2">
              <ShieldAlert size={16} />
              <span>All communication must stay within BYN.</span>
            </div>
          </motion.div>
        )}

        {/* CHAT BOX */}
        <div
          ref={chatBoxRef}
          className="flex-1 bg-white rounded-2xl p-4 shadow-inner border overflow-y-auto"
        >
          {messages.length === 0 ? (
            <p className="text-gray-400 text-center mt-10">Start chatting 👋</p>
          ) : (
            messages.map((msg, idx) => {
              const isMine = normalizeId(msg.senderId) === String(workerId);
              const messageTime =
                msg.timestamp || msg.createdAt || new Date().toISOString();

              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className={`mb-2 flex ${isMine ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`px-4 py-2 rounded-2xl shadow-sm max-w-xs sm:max-w-sm ${
                      isMine
                        ? "bg-blue-600 text-white rounded-br-none"
                        : "bg-gray-100 text-gray-900 rounded-bl-none"
                    }`}
                  >
                    {/* TEXT */}
                    {msg.message && <p>{msg.message}</p>}

                    {/* 🔥 IMAGE BUBBLE */}
                    {msg.imageUrl && (
                      <img
                        src={`${API_BASE}${msg.imageUrl}`}
                        alt="sent"
                        className="mt-2 rounded-lg max-w-[180px] cursor-pointer hover:opacity-80 transition"
                        onClick={() =>
                          setFullscreenImage(`${API_BASE}${msg.imageUrl}`)
                        }
                      />
                    )}

                    <p className="text-xs text-right mt-1 opacity-70">
                      {formatTime(messageTime)}
                    </p>
                  </div>
                </motion.div>
              );
            })
          )}

          {isTyping && (
            <p className="text-xs text-gray-500 italic mt-2 animate-pulse">
              Customer is typing...
            </p>
          )}
        </div>

        {/* INPUT */}
        <div className="mt-4 flex items-center bg-white rounded-full border shadow px-4 py-2">
          <input
            type="file"
            accept="image/*"
            id="chatImageUpload"
            hidden
            onChange={handleImageUpload}
          />

          <button
            onClick={() => document.getElementById("chatImageUpload").click()}
            className="mr-3 text-gray-600 hover:text-gray-900"
          >
            <ImageIcon size={22} />
          </button>

          <input
            type="text"
            value={input}
            onChange={handleTyping}
            onKeyDown={(e) => e.key === "Enter" && sendMessage()}
            placeholder="Type your message..."
            className="flex-grow px-3 py-2 text-gray-800 bg-transparent focus:outline-none"
          />
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={sendMessage}
            className="bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full shadow-md transition-all duration-150"
          >
            <Send size={18} />
          </motion.button>
        </div>
      </div>

      {/* 🔥 FULLSCREEN IMAGE VIEWER */}
      {fullscreenImage && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50"
          onClick={() => setFullscreenImage(null)}
        >
          <img
            src={fullscreenImage}
            className="max-w-full max-h-full rounded-xl shadow-xl"
            alt=""
          />
        </div>
      )}
    </div>
  );
};

export default ChatPage;
